import { inject } from '@angular/core';
import { CanMatchFn, Route } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, map, of, switchMap, take, tap } from 'rxjs';
import { common_urls } from 'src/environments/environment';
import { ProcedureConfigService } from 'src/app/service/procedure-config.service';
import { ProcedureBackend } from 'src/app/service/procedure-config.constants';
import { CwsUrlNavigator } from '../cws-url-navigator';

export const CWS_HOME_URL = `${common_urls.cwsBaseUrl}/home?From=auth&cws=1`;

function isValidProcedureConfig(config: unknown): config is {
  enabled: boolean;
  backend: ProcedureBackend;
} {
  if (!config || typeof config !== 'object') {
    return false;
  }

  const candidate = config as { enabled?: unknown; backend?: unknown };
  return (
    typeof candidate.enabled === 'boolean' &&
    Object.values(ProcedureBackend).includes(candidate.backend as ProcedureBackend)
  );
}

export const procedureConfigCanMatch: CanMatchFn = (route: Route) => {
  const procedureConfigService = inject(ProcedureConfigService);
  const oidcSecurityService = inject(OidcSecurityService);
  const procedureName = route.data?.['procedureConfigName'];
  const sfdcTarget = route.data?.['sfdcTarget'];
  const sfdcIncludeCws = route.data?.['sfdcIncludeCws'] === true;
  const sfdcUseHomeNavigation = route.data?.['sfdcUseHomeNavigation'] === true;

  if (typeof procedureName !== 'string' || typeof sfdcTarget !== 'string') {
    window.location.href = CWS_HOME_URL;
    return of(false);
  }

  return oidcSecurityService.checkAuth().pipe(
    take(1),
    switchMap(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        // Not authenticated yet - canMatch runs before canActivate, so let
        // autoLoginPartialRoutesGuard start the login redirect instead of failing
        // this check closed before authentication has a chance to run.
        return of(true);
      }

      return procedureConfigService.load(procedureName).pipe(
        tap((config) => {
          if (!isValidProcedureConfig(config) || !config.enabled) {
            window.location.href = CWS_HOME_URL;
          } else if (config.backend === ProcedureBackend.Sfdc) {
            // Direct deep links have no call-center menu context to key off, so use the
            // regular (non-apex) URL form - the same default used for non-call-center users.
            window.location.href = sfdcUseHomeNavigation
              ? CwsUrlNavigator.CWS_Home_Navigation(sfdcTarget)
              : CwsUrlNavigator.CWS_Navigation_URL(sfdcTarget, true, true, false, sfdcIncludeCws);
          }
        }),
        map((config) => isValidProcedureConfig(config) && config.enabled && config.backend === ProcedureBackend.Angular),
        catchError(() => {
          window.location.href = CWS_HOME_URL;
          return of(false);
        })
      );
    })
  );
};